import * as React from "react";
import { Grid, Button } from "semantic-ui-react";

import { invalidDateAnswer } from "@teamprevent/common/questionnaires/components/questions/OldDateQuestion";

import { MatchFieldTypes, PartiallyMatchedEmployeeDetailModel } from "../model";
import { EmployeePartialMatch } from "../model";

import { PartiallyMatchedEmployeeRecordCard } from "./PartiallyMatchedEmployeeRecordCard";
import { PartiallyMatchedFormDetails } from "./PartiallyMatchedFormDetails";
import { CreateNewEmployeeConfirmationModal } from "./CreateNewEmployeeConfirmationModal";
import { MatchExistingEmployeeConfirmationModal } from "./MatchExistingEmployeeConfirmationModal";

export interface DetailProps {
    partiallyMatchedEmployee: PartiallyMatchedEmployeeDetailModel;
    moreRoute: string;
    title: string;
    showAdditionalMatchDetails?: boolean;
    isSubmitting: boolean;
    customerId: string;
    referralId: string;
}

export interface DetailDispatchProps {
    matchEmployeeToNewEmployeeRecord: (referralCustomerId: string, referralId: string) => void;
    matchToEmployeeRecord: (formCustomerId: string, referralId: string, employeeRecordCustomerId: string, employeeRecordId: string) => void;
    rerunEmployeeMatching: (referralCustomerId: string, referralId: string) => void;
    loadPartiallyMatchedDetails: (customerId: string, referralId: string) => void;
}

export interface State {
    currentEmployeeIndex: number;
    currentEmployee?: EmployeePartialMatch;
    currentFieldClassNames: Record<string, string> & string[];
    createNewEmployeeConfirmationModalOpen: boolean;
    matchExistingEmployeeConfirmationModalOpen: boolean;
}

export class Detail extends React.Component<DetailProps & DetailDispatchProps, State> {
    constructor(props: DetailProps & DetailDispatchProps) {
        super(props);

        this.state = {
            currentEmployeeIndex: -1,
            currentEmployee: null,
            currentFieldClassNames: [] as Record<string, string> & string[],
            createNewEmployeeConfirmationModalOpen: false,
            matchExistingEmployeeConfirmationModalOpen: false
        };

        this.rerunEmployeeMatching = this.rerunEmployeeMatching.bind(this);
        props.loadPartiallyMatchedDetails(props.customerId, props.referralId);
    }

    public componentDidUpdate() {
        if (!this.state.currentEmployee) {
            const currentEmployeeIndex = this.props.partiallyMatchedEmployee.partialMatchEmployeeRecords?.length > 0 ? 0 : -1;
            const currentEmployee = this.props.partiallyMatchedEmployee.partialMatchEmployeeRecords?.length > 0
                ? this.props.partiallyMatchedEmployee.partialMatchEmployeeRecords[0]
                : null;

            this.setState({
                ...this.state,
                currentEmployeeIndex,
                currentEmployee,
                currentFieldClassNames: currentEmployee != null
                    ? this.getFieldClassNames(currentEmployee)
                    : [] as Record<string, string> & string[] // eslint-disable-line eqeqeq
            });
        }
    }

    private getFieldClassNames(employee: EmployeePartialMatch): Record<string, string> & string[] {
        const fieldNames = Object.values(MatchFieldTypes) as (typeof MatchFieldTypes[keyof typeof MatchFieldTypes])[];
        let fieldClassNames = [] as Record<string, string> & string[];

        if (!employee) {
            return fieldClassNames;
        }

        fieldNames.forEach((fieldName) => {
            const compareValues = [
                this.props.partiallyMatchedEmployee[fieldName]
                    ? this.props.partiallyMatchedEmployee[fieldName].toString()
                    : "",
                employee[fieldName]
                    ? employee[fieldName].toString()
                    : "",
            ];

            const unmatchedClassName = "unmatched-field";
            let fieldNameMatchClass = "";

            if (compareValues.every(v => v.length === 0) || compareValues.every(v => v === invalidDateAnswer)) {
                fieldNameMatchClass = unmatchedClassName;
            }
            else {
                fieldNameMatchClass = new Set(compareValues).size !== 1 ? unmatchedClassName : "";
            }

            fieldClassNames = {
                ...fieldClassNames,
                [fieldName]:
                fieldNameMatchClass,
            };
        });

        return fieldClassNames;
    }

    private previousPage() {
        const currentEmployeeIndex =
            this.state.currentEmployeeIndex > 0
                ? this.state.currentEmployeeIndex - 1
                : (this.props.partiallyMatchedEmployee.partialMatchEmployeeRecords.length > 0 ? 0 : -1);
        const currentEmployee = this.props.partiallyMatchedEmployee.partialMatchEmployeeRecords.length > 0
            ? this.props.partiallyMatchedEmployee.partialMatchEmployeeRecords[currentEmployeeIndex]
            : null;
        const currentFieldClassNames = currentEmployee ? this.getFieldClassNames(currentEmployee) : [] as string[] & Record<string, string>;
        this.setState({
            ...this.state,
            currentEmployeeIndex,
            currentEmployee,
            currentFieldClassNames
        });
    }

    private nextPage() {
        const currentEmployeeIndex =
            this.props.partiallyMatchedEmployee.partialMatchEmployeeRecords.length > (this.state.currentEmployeeIndex + 1)
                ? this.state.currentEmployeeIndex + 1
                : this.state.currentEmployeeIndex;

        const currentEmployee = this.props.partiallyMatchedEmployee.partialMatchEmployeeRecords[currentEmployeeIndex];
        const currentFieldClassNames = currentEmployee ? this.getFieldClassNames(currentEmployee) : [] as string[] & Record<string, string>;
        this.setState({
            ...this.state,
            currentEmployeeIndex,
            currentEmployee,
            currentFieldClassNames
        });
    }

    private onCreateNewEmployeeConfirmationModalOpen() {
        this.setState({
            ...this.state,
            createNewEmployeeConfirmationModalOpen: true
        });
    }

    private onCreateNewEmployeeConfirmationModalClose() {
        this.setState({
            ...this.state,
            createNewEmployeeConfirmationModalOpen: false
        });
    }

    private onMatchExistingEmployeeConfirmationModalOpen() {
        this.setState({
            ...this.state,
            matchExistingEmployeeConfirmationModalOpen: true
        });
    }

    private onMatchExistingEmployeeConfirmationModalClose() {
        this.setState({
            ...this.state,
            matchExistingEmployeeConfirmationModalOpen: false
        });
    }

    private async rerunEmployeeMatching() {
        // The await here is an anti-pattern but is allowed. Ideally we rewrite this component to a FC one with a useEffect so we can
        // force a refresh of the data after the rerun is done.
        await this.props.rerunEmployeeMatching(this.props.partiallyMatchedEmployee.customerId, this.props.partiallyMatchedEmployee.id);
        this.setState({
            ...this.state,
            currentEmployee: null,
        });
    }

    public render() {

        const hasEmployeeMatches = this.props.partiallyMatchedEmployee.partialMatchEmployeeRecords?.length > 0;

        return (
            <Grid container centered className="nomargintop">
                <Grid.Row>
                    <Grid.Column width={4} className="partial-matches-column" stretched>
                        <PartiallyMatchedFormDetails
                            formDetails={this.props.partiallyMatchedEmployee}
                            moreRoute={this.props.moreRoute}
                            showAdditionalMatchDetails={this.props.showAdditionalMatchDetails}
                            title={this.props.title}
                        />
                        <div>
                            <Button
                                onClick={() => this.onCreateNewEmployeeConfirmationModalOpen()}
                                className="create-new-employee-record-button"
                                loading={this.props.isSubmitting}
                                disabled={this.props.isSubmitting}
                            >
                                Add as a new employee record
                            </Button>
                        </div>
                        <CreateNewEmployeeConfirmationModal
                            open={this.state.createNewEmployeeConfirmationModalOpen}
                            title={this.props.title}
                            onClose={() => this.onCreateNewEmployeeConfirmationModalClose()}
                            matchEmployeeToNewEmployeeRecord={this.props.matchEmployeeToNewEmployeeRecord}
                            formDetails={this.props.partiallyMatchedEmployee}
                            showAdditionalMatchDetails={this.props.showAdditionalMatchDetails}
                            isSubmitting={this.props.isSubmitting}
                        />
                    </Grid.Column>
                    {this.props.partiallyMatchedEmployee.partialMatchEmployeeRecords &&
                        this.props.partiallyMatchedEmployee.partialMatchEmployeeRecords.length > 0 &&
                        this.state.currentEmployee &&
                        <Grid.Column width={4} className="partial-matches-column" stretched>
                            <PartiallyMatchedEmployeeRecordCard
                                employeeRecord={this.state.currentEmployee}
                                fieldClassNames={this.state.currentFieldClassNames}
                                referralCustomerId={this.props.partiallyMatchedEmployee.customerId}
                                referralId={this.props.partiallyMatchedEmployee.id}
                                key={this.state.currentEmployee.employeeRecordId}
                                showAdditionalMatchDetails={this.props.showAdditionalMatchDetails}
                            />
                            <div>
                                <Button basic
                                    icon="chevron left"
                                    onClick={() => this.previousPage()}
                                    disabled={this.state.currentEmployeeIndex <= 0}
                                />
                                <Button basic
                                    icon="chevron right"
                                    onClick={() => this.nextPage()}
                                    disabled={this.state.currentEmployeeIndex >=
                                        (this.props.partiallyMatchedEmployee.partialMatchEmployeeRecords.length - 1)}
                                />
                                <label>
                                    Showing {this.state.currentEmployeeIndex + 1} of
                                    &nbsp;{this.props.partiallyMatchedEmployee.partialMatchEmployeeRecords.length} records
                                </label>
                            </div>
                            <MatchExistingEmployeeConfirmationModal
                                open={this.state.matchExistingEmployeeConfirmationModalOpen}
                                title={this.props.title}
                                onClose={() => this.onMatchExistingEmployeeConfirmationModalClose()}
                                formDetails={this.props.partiallyMatchedEmployee}
                                showAdditionalMatchDetails={this.props.showAdditionalMatchDetails}
                                matchToEmployeeRecord={this.props.matchToEmployeeRecord}
                                partialMatchedEmployee={this.state.currentEmployee}
                                fieldClassNames={this.state.currentFieldClassNames}
                                isSubmitting={this.props.isSubmitting}
                            />
                        </Grid.Column>
                    }
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column width={4} textAlign="center">
                        <Button
                            primary
                            onClick={() => this.onMatchExistingEmployeeConfirmationModalOpen()}
                            loading={this.props.isSubmitting}
                            disabled={this.props.isSubmitting || !hasEmployeeMatches}
                        >
                            Match the above entries
                        </Button>
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column width={4} textAlign="center">
                        <Button
                            onClick={this.rerunEmployeeMatching}
                            className="create-new-employee-record-button"
                            loading={this.props.isSubmitting}
                            disabled={this.props.isSubmitting}
                        >
                            Rerun
                        </Button>
                    </Grid.Column>
                </Grid.Row>
            </Grid >
        );
    }
}
